import { useCallback, useEffect, useState } from "react";

import { useQuery as defaultUseQuery } from "@apollo/react-hooks";
import { isFunction } from "lodash";

import { ApolloInjection } from "@zapier/checkout/esm/types";
import { Currency, defaultCurrency, useCurrencies } from "@zapier/currency";

type Input = {
  currency?: string;
  onSelectCurrency?: (currency: string) => void;
} & ApolloInjection;

export const useSelectedCurrency = ({
  currency,
  onSelectCurrency,
  useQuery = defaultUseQuery,
}: Input) => {
  // If currency is provided, set it as the initial dropdown value with 'USD' as the default backup.
  const [selectedCurrency, setSelectedCurrency] = useState(
    currency ?? defaultCurrency.name
  );
  const { isLoading: isLoadingCurrencies, currencies } = useCurrencies({
    useQuery,
  });

  // Get the entire currency object from useCurrencies so we can pass that back
  const currentCurrency =
    currencies.find((c: Currency) => c.name === selectedCurrency) ??
    defaultCurrency;

  // If currency is being controlled externally, set state to given value
  useEffect(() => {
    if (currency && isFunction(onSelectCurrency)) {
      setSelectedCurrency(currency);
    }
  }, [currency, onSelectCurrency, setSelectedCurrency]);

  const onSelectCurrencyCallback = useCallback(
    (newCurrency: Currency) => {
      // This function is only called when provided, meaning it's being controlled externally
      if (isFunction(onSelectCurrency)) {
        onSelectCurrency(newCurrency.name);
      }
      setSelectedCurrency(newCurrency.name);
    },
    [onSelectCurrency, setSelectedCurrency]
  );

  return {
    selectedCurrency: currentCurrency,
    setSelectedCurrency: onSelectCurrencyCallback,
    isLoading: isLoadingCurrencies,
  };
};
